// src/app/app.routes.ts
import { Routes } from '@angular/router';
import { LandingComponent } from './components/pages/landing/landing.component';
import { NotFoundComponent } from './components/pages/not-found/not-found.component';
// import { ProductComponent } from './components/pages/products/product.component';
import { I2ReportComponent } from './components/pages/i2report/i2report.component';
import { BlogResolver } from './core/services/blog-resolver.service';
import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { RedirectService } from './core/services/redirect.service';

// Define route guard for subscription products
const subscriptionGuard = () => {
  // Implement your subscription check logic here
  return true;
};

// Legacy URL redirect handler
const legacyUrlHandler = () => {
  const redirectService = inject(RedirectService);
  const platformId = inject(PLATFORM_ID);
  
  // Only run in browser environment
  if (isPlatformBrowser(platformId)) {
    // Get the full URL from the browser
    const url = window.location.href;
    
    // Check if this is a legacy URL that needs to be redirected
    if (url.includes('i2report/index.cfm')) {
      redirectService.handleLegacyUrl(url);
      return false; // Prevent the route from activating
    }
  }
  
  return true; // Allow the route to activate
};

export const routes: Routes = [
  // Landing page
  {
    path: '',
    loadComponent: () => import('./components/pages/landing/landing.component').then(m => m.LandingComponent),
    title: 'Industry Intelligence Platform'
  },

  // Legacy URL handler - Direct component loading without redirection
  {
    path: 'i2report/index.cfm',
    loadComponent: () => import('./components/pages/i2report/i2report.component').then(m => m.I2ReportComponent),
    title: 'Industry Report - Industry Intelligence'
  },

  // Modern URL format
  {
    path: 'i2report/:id',
    loadComponent: () => import('./components/pages/i2report/i2report.component').then(m => m.I2ReportComponent),
    title: 'Industry Report - Industry Intelligence'
  },

  // Generic redirect handler for all legacy URLs
  {
    path: 'redirect',
    loadComponent: () => import('./components/redirect/redirect.component').then(m => m.RedirectComponent),
    title: 'Redirecting...'
  },

  // Keep the old article route for backward compatibility but redirect to i2report
  {
    path: 'article/:id',
    redirectTo: route => `/i2report/${route.params['id']}`
  },

  // Product Routes
  {
    path: 'legislation-monitor',
    loadComponent: () => import('./components/pages/products/legislation-monitor/legislation-monitor.component')
      .then(m => m.LegislationMonitorComponent)
  },
  {
    path: 'competitive-edge',
    loadComponent: () => import('./components/pages/products/competitive-edge/competitive-edge.component')
      .then(m => m.CompetitiveEdgeComponent)
  },
  {
    path: 'customer-insight',
    loadComponent: () => import('./components/pages/products/customer-insight/customer-insight.component')
      .then(m => m.CustomerInsightComponent)
  },
  {
    path: 'supplier-iq',
    loadComponent: () => import('./components/pages/products/supplier-iq/supplier-iq.component')
      .then(m => m.SupplierIQComponent)
  },
  {
    path: 'daily-market-report',
    loadComponent: () => import('./components/pages/products/daily-market-report/daily-market-report.component')
      .then(m => m.DailyMarketReportComponent)
  },
  {
    path: 'executive-summary',
    loadComponent: () => import('./components/pages/products/executive-summary/executive-summary.component')
      .then(m => m.ExecutiveSummaryComponent),
    canActivate: [subscriptionGuard]
  },
  {
    path: 'sustainable-chemicals',
    loadComponent: () => import('./components/pages/products/sustainable-chemicals/sustainable-chemicals.component')
      .then(m => m.SustainableChemicalsComponent),
    canActivate: [subscriptionGuard]
  },

  // Industry Routes
  {
    path: 'industries/:slug',
    loadComponent: () => import('./components/pages/solutions/industries/industry-detail.component')
      .then(m => m.IndustryDetailComponent)
  },

  // Department Routes
  {
    path: 'departments/:slug',
    loadComponent: () => import('./components/pages/solutions/departments/department-detail.component')
      .then(m => m.DepartmentDetailComponent)
  },

  // Company Type Routes
  {
    path: 'company-types/:slug',
    loadComponent: () => import('./components/pages/solutions/company-types/company-type-detail.component')
      .then(m => m.CompanyTypeDetailComponent)
  },

  // Resource Routes
  {
    path: 'blog',
    children: [
      {
        path: '', loadComponent: () => import('./components/pages/blog/blog-list/blog-list.component').then(m => m.BlogListComponent),
        title: 'Blog - Industry Intelligence'
      },
      {
        path: ':slug', resolve: { blog: BlogResolver }, loadComponent: () => import('./components/pages/blog/blog.component').then(m => m.BlogComponent),
        title: 'Blog Post - Industry Intelligence'
      },
    ]
  },
  {
    path: 'press-releases', loadComponent: () => import('./components/pages/resources/press-release/press-release.component').then(m => m.PressReleaseComponent)
  },
  {
    path: 'use-cases', loadComponent: () => import('./components/pages/resources/use-case/use-case.component').then(m => m.UseCaseComponent)
  },
  {
    path: 'client-stories', loadComponent: () => import('./components/pages/resources/client-story/client-story.component').then(m => m.ClientStoryComponent)
  },
  {
    path: 'faq', loadComponent: () => import('./components/pages/resources/faq/faq.component').then(m => m.FAQComponent)
  },
  {
    path: 'changelog', loadComponent: () => import('./components/pages/resources/changelog/changelog.component').then(m => m.ChangelogComponent)
  },
  {
    path: 'brand-guidelines', loadComponent: () => import('./components/pages/resources/brand-guidelines/brand-guidelines.component').then(m => m.BrandGuidelinesComponent)
  },

  // Company Routes
  {
    path: 'about', loadComponent: () => import('./components/pages/company/about/about.component').then(m => m.AboutComponent)
  },
  {
    path: 'careers', loadComponent: () => import('./components/pages/company/careers/careers.component').then(m => m.CareersComponent)
  },
  {
    path: 'contact', loadComponent: () => import('./components/pages/company/contact/contact.component').then(m => m.ContactComponent)
  },

  // Legal Routes
  {
    path: 'privacy-policy', loadComponent: () => import('./components/pages/legal/privacy-policy/privacy-policy.component').then(m => m.PrivacyPolicyComponent)
  },
  {
    path: 'terms', loadComponent: () => import('./components/pages/legal/terms/terms.component').then(m => m.TermsComponent)
  },
  {
    path: 'service-agreement', loadComponent: () => import('./components/pages/legal/service-agreement/service-agreement.component').then(m => m.ServiceAgreementComponent)
  },
  {
    path: 'privacy-choices', loadComponent: () => import('./components/pages/legal/privacy-choices/privacy-choices.component').then(m => m.PrivacyChoicesComponent)
  },

  // 404 Route
  {
    path: '**', loadComponent: () => import('./components/pages/not-found/not-found.component').then(m => m.NotFoundComponent)
  }
];